import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography, alpha, Button, IconButton, Tooltip, Divider } from '@mui/material';
import {
  PeopleAltRounded,
  SensorsRounded,
  WifiTetheringRounded,
  NotificationsActiveRounded,
  ArrowForwardRounded,
  OpenInFullRounded,
  RefreshRounded,
  BoltRounded,
  InsightsRounded,
  WarningAmberRounded,
  ErrorOutlineRounded,
  InfoOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { errorMessage } from '../lib/api';
import { relativeTime } from '../lib/format';
import { palette } from '../theme';
import { GlassPanel, SectionTitle, StatusPill, EmptyState, PanelLoader } from '../components/ui/GlassPanel';
import { StatCard3D } from '../components/ui/StatCard3D';
import { HoloLineChart, HoloBarChart } from '../components/charts/HoloChart';
import DeviceCluster3D from '../components/three/DeviceCluster3D';
import { useAuth } from '../App';
import { useToast } from '../components/ui/ToastProvider';

const isoDay = (date) => date.toISOString().slice(0, 10);

const SEVERITY_STYLE = {
  critical: { color: palette.danger, icon: <ErrorOutlineRounded sx={{ fontSize: 16 }} /> },
  warning: { color: palette.warning, icon: <WarningAmberRounded sx={{ fontSize: 16 }} /> },
  info: { color: palette.primary, icon: <InfoOutlined sx={{ fontSize: 16 }} /> },
};

function AlertIndicator({ critical = 0, warning = 0, info = 0 }) {
  const total = critical + warning + info;
  const segments = [
    { value: critical, color: palette.danger },
    { value: warning, color: palette.warning },
    { value: info, color: palette.primary },
  ].filter((segment) => segment.value > 0);

  return (
    <Box sx={{ position: 'relative', mt: 1.75 }}>
      <Box sx={{ display: 'flex', height: 6, borderRadius: 4, overflow: 'hidden', background: alpha('#334155', 0.35) }}>
        {segments.map((segment) => (
          <Box
            key={segment.color}
            sx={{
              width: `${total ? (segment.value / total) * 100 : 0}%`,
              background: `linear-gradient(90deg, ${alpha(segment.color, 0.65)}, ${segment.color})`,
              boxShadow: `0 0 12px ${alpha(segment.color, 0.8)}`,
              transition: 'width 800ms cubic-bezier(0.22,0.9,0.28,1)',
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
        {[
          { label: 'Critical', value: critical, color: palette.danger },
          { label: 'Warning', value: warning, color: palette.warning },
          { label: 'Info', value: info, color: palette.primary },
        ].map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: item.color, boxShadow: `0 0 8px ${item.color}` }} />
            <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>
              {item.label} <Box component="span" className="mono" sx={{ color: 'text.primary', fontWeight: 600 }}>{item.value}</Box>
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function StatusOrb({ online, total }) {
  const ratio = total ? online / total : 0;
  return (
    <Box sx={{ position: 'relative', width: 74, height: 74, marginTop: 0.5 }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: `conic-gradient(${palette.success} ${ratio * 360}deg, ${alpha('#334155', 0.45)} 0deg)`,
          mask: 'radial-gradient(circle, transparent 58%, black 60%)',
          WebkitMask: 'radial-gradient(circle, transparent 58%, black 60%)',
          animation: 'holoSpin 6s linear infinite',
          filter: `drop-shadow(0 0 10px ${alpha(palette.success, 0.6)})`,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 12,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background: `radial-gradient(circle, ${alpha(palette.success, 0.22)}, transparent 70%)`,
        }}
      >
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: '50%',
            bgcolor: palette.success,
            boxShadow: `0 0 14px ${palette.success}`,
            animation: 'holoPulse 2.4s ease-in-out infinite',
          }}
        />
      </Box>
    </Box>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [overview, setOverview] = useState(null);
  const [operatorDevices, setOperatorDevices] = useState([]);
  const [operatorReport, setOperatorReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    try {
      const response = await api.get('/admin/overview');
      setOverview(response.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('overview', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const loadOperatorScope = useCallback(async () => {
    try {
      const [devicesResponse, reportResponse] = await Promise.allSettled([
        api.get('/devices/', { params: { limit: 100 } }),
        api.get('/reports', { params: { start: isoDay(new Date(Date.now() - 7 * 86400000)), end: isoDay(new Date()) } }),
      ]);
      if (devicesResponse.status === 'fulfilled') {
        setOperatorDevices(devicesResponse.value?.data || []);
      } else {
      }
      if (reportResponse.status === 'fulfilled') setOperatorReport(reportResponse.value?.data || null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('operator scope failed', error && error.message);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), 15000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    if (isAdmin) return;
    loadOperatorScope();
    const interval = setInterval(loadOperatorScope, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, loadOperatorScope, user]);

  const activityChart = useMemo(() => {
    const activity = overview?.activity || [];
    return {
      labels: activity.map((point) => point.label),
      datasets: [
        {
          label: 'Telemetry messages',
          data: activity.map((point) => point.messages),
          color: palette.primary,
        },
        {
          label: 'Alerts raised',
          data: activity.map((point) => point.alerts),
          color: palette.danger,
        },
      ],
    };
  }, [overview]);

  const statusChart = useMemo(() => {
    const breakdown = overview?.status_breakdown || [];
    return {
      labels: breakdown.map((item) => item.label),
      datasets: [
        {
          label: 'Devices',
          data: breakdown.map((item) => item.value),
          color: palette.primary,
        },
      ],
    };
  }, [overview]);

  const runOperation = async (device, operation) => {
    try {
      await api.post(`/devices/${device.device_id}/command`, {
        action: operation.code,
        label: operation.label,
      });
      toast.success(`"${operation.label}" dispatched to ${device.device_id}.`);
    } catch (error) {
      toast.error(errorMessage(error, 'The remote operation could not be dispatched.'));
    }
  };

  if (loading) {
    return (
      <GlassPanel>
        <PanelLoader label="Synchronising fleet telemetry" />
      </GlassPanel>
    );
  }

  const devices = overview?.devices || {};
  const alerts = overview?.alerts || {};
  const users = overview?.users || {};
  const recentAlerts = overview?.recent_alerts || [];
  const recentDevices = overview?.recent_devices || [];
  const fleet = overview?.fleet || [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* summary strip */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} lg={3}>
          <StatCard3D
            title={isAdmin ? 'Total Users' : 'Assigned Devices'}
            value={isAdmin ? users.total || 0 : devices.total || 0}
            icon={<PeopleAltRounded />}
            accent={palette.secondary}
            caption={
              isAdmin
                ? `${users.active || 0} active operators · ${Math.max((users.total || 0) - (users.active || 0), 0)} suspended`
                : 'Endpoints currently allocated to your account'
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <StatCard3D
            title="Total Devices"
            value={devices.total || 0}
            icon={<SensorsRounded />}
            accent={palette.primary}
            caption={`${devices.signage || 0} signage endpoints · ${devices.updating || 0} updating`}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Box
            sx={{
              height: '100%',
              borderRadius: 4,
              padding: 2.75,
              border: `1px solid ${alpha(palette.success, 0.18)}`,
              background: `linear-gradient(155deg, ${alpha('#101B36', 0.82)}, ${alpha('#070C1C', 0.86)})`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 20px 50px -28px rgba(2,6,23,0.95), inset 0 1px 0 ${alpha('#FFFFFF', 0.04)}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(130% 100% at 0% 0%, ${alpha(palette.success, 0.16)}, transparent 58%)`,
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.16em', color: 'text.secondary', textTransform: 'uppercase' }}>
                  Online Devices
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 1.5 }}>
                  <Typography className="mono" sx={{ fontSize: '2.1rem', fontWeight: 500, lineHeight: 1, textShadow: `0 0 26px ${alpha(palette.success, 0.5)}` }}>
                    {devices.online || 0}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 600 }}>
                    / {devices.total || 0}
                  </Typography>
                </Box>
              </Box>
              <StatusOrb online={devices.online || 0} total={devices.total || 0} />
            </Box>
            <Typography sx={{ position: 'relative', mt: 1, fontSize: '0.72rem', color: 'text.secondary' }}>
              {devices.offline || 0} offline · {devices.updating || 0} updating
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Box
            sx={{
              height: '100%',
              borderRadius: 4,
              padding: 2.75,
              border: `1px solid ${alpha(palette.danger, 0.18)}`,
              background: `linear-gradient(155deg, ${alpha('#101B36', 0.82)}, ${alpha('#070C1C', 0.86)})`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 20px 50px -28px rgba(2,6,23,0.95), inset 0 1px 0 ${alpha('#FFFFFF', 0.04)}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: `radial-gradient(130% 100% at 100% 0%, ${alpha(palette.danger, 0.16)}, transparent 58%)`,
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
              <Box>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.16em', color: 'text.secondary', textTransform: 'uppercase' }}>
                  Active Alerts
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 1.5 }}>
                  <Typography className="mono" sx={{ fontSize: '2.1rem', fontWeight: 500, lineHeight: 1, textShadow: `0 0 26px ${alpha(palette.danger, 0.5)}` }}>
                    {alerts.active || 0}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2.5,
                  color: palette.danger,
                  background: alpha(palette.danger, 0.12),
                  border: `1px solid ${alpha(palette.danger, 0.3)}`,
                  boxShadow: `inset 0 0 20px ${alpha(palette.danger, 0.18)}`,
                }}
              >
                <NotificationsActiveRounded sx={{ fontSize: 21 }} />
              </Box>
            </Box>
            <AlertIndicator critical={alerts.critical || 0} warning={alerts.warning || 0} info={alerts.info || 0} />
          </Box>
        </Grid>
      </Grid>

      {/* charts */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <GlassPanel sx={{ p: 3, height: '100%' }}>
            <SectionTitle
              title="Device Activity"
              subtitle="Real-time device activity over time"
              icon={<WifiTetheringRounded sx={{ fontSize: 18 }} />}
              action={
                <Tooltip title="Refresh">
                  <IconButton size="small" onClick={() => load(true)} sx={{ color: 'text.secondary' }}>
                    <RefreshRounded sx={{ fontSize: 17, animation: refreshing ? 'holoSpin 900ms linear infinite' : 'none' }} />
                  </IconButton>
                </Tooltip>
              }
            />
            <Box sx={{ mt: 2.5 }}>
              <HoloLineChart labels={activityChart.labels} datasets={activityChart.datasets} height={268} legend yTitle="Events" />
            </Box>
          </GlassPanel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <GlassPanel sx={{ p: 3, height: '100%' }}>
            <SectionTitle
              title="Device Status"
              subtitle="Online, offline and updating devices"
              icon={<SensorsRounded sx={{ fontSize: 18 }} />}
            />
            <Box sx={{ mt: 2.5 }}>
              <HoloBarChart labels={statusChart.labels} datasets={statusChart.datasets} height={268} legend />
            </Box>
          </GlassPanel>
        </Grid>
      </Grid>

      {/* 3D cluster + recent alerts */}
      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <GlassPanel sx={{ p: 3, height: '100%' }}>
            <SectionTitle
              title="Device Overview"
              subtitle="Interactive 3D fleet lattice — rotate, hover, select and zoom"
              icon={<SensorsRounded sx={{ fontSize: 18 }} />}
              action={
                <Button
                  size="small"
                  endIcon={<ArrowForwardRounded sx={{ fontSize: 15 }} />}
                  onClick={() => navigate('/devices')}
                  sx={{ fontSize: '0.74rem', color: 'text.secondary' }}
                >
                  Manage fleet
                </Button>
              }
            />
            <Box sx={{ mt: 2 }}>
              {fleet.length === 0 ? (
                <EmptyState
                  icon={<SensorsRounded />}
                  title="No devices registered"
                  description="Provision endpoints from the device console to populate the fleet lattice."
                  minHeight={260}
                />
              ) : (
                <DeviceCluster3D
                  devices={fleet}
                  height={380}
                  onSelect={(device) => navigate(`/devices/${device.device_id}`)}
                />
              )}
            </Box>
          </GlassPanel>
        </Grid>

        <Grid item xs={12} lg={4}>
          <GlassPanel sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <SectionTitle
              title="Recent Alerts"
              subtitle="Latest 5 fleet events"
              icon={<NotificationsActiveRounded sx={{ fontSize: 18 }} />}
              action={
                <Button
                  size="small"
                  endIcon={<ArrowForwardRounded sx={{ fontSize: 15 }} />}
                  onClick={() => navigate('/alerts')}
                  sx={{ fontSize: '0.74rem', color: 'text.secondary' }}
                >
                  View all
                </Button>
              }
            />
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.25, flex: 1 }}>
              {recentAlerts.length === 0 ? (
                <EmptyState icon={<NotificationsActiveRounded />} title="No active alerts" description="The fleet is operating within all defined thresholds." minHeight={220} />
              ) : (
                recentAlerts.map((alert) => {
                  const style = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.info;
                  return (
                    <Box
                      key={alert.id}
                      onClick={() => navigate('/alerts')}
                      sx={{
                        position: 'relative',
                        p: 1.6,
                        borderRadius: 3,
                        cursor: 'pointer',
                        border: `1px solid ${alpha(style.color, 0.24)}`,
                        background: `linear-gradient(140deg, ${alpha(style.color, 0.1)}, ${alpha('#070C1C', 0.55)})`,
                        transition: 'transform 240ms cubic-bezier(0.22,0.9,0.28,1), border-color 240ms ease',
                        '&:hover': { transform: 'translateY(-2px)', borderColor: alpha(style.color, 0.5) },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 14,
                          bottom: 14,
                          width: 2,
                          borderRadius: 2,
                          background: style.color,
                          boxShadow: `0 0 10px ${style.color}`,
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
                        <Box sx={{ color: style.color, display: 'flex' }}>{style.icon}</Box>
                        <Typography className="mono" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                          {alert.device_name}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                          {relativeTime(alert.created_at)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.73rem', color: 'text.secondary', lineHeight: 1.45, pl: 3.1 }}>
                        {alert.message}
                      </Typography>
                    </Box>
                  );
                })
              )}
            </Box>
          </GlassPanel>
        </Grid>
      </Grid>


      {/* operator console: remote operations + device data report */}
      {!isAdmin && (
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={7}>
            <GlassPanel sx={{ p: 3, height: '100%' }}>
              <SectionTitle
                title="Remote Operations"
                subtitle="Send commands to the devices assigned to you"
                icon={<BoltRounded sx={{ fontSize: 18 }} />}
                action={
                  <StatusPill
                    label={`${operatorDevices.length} endpoint${operatorDevices.length === 1 ? '' : 's'}`}
                    tone="info"
                    pulse={false}
                  />
                }
              />

              <Box sx={{ mt: 2.25, display: 'grid', gap: 1.25 }}>
                {operatorDevices.length === 0 && (
                  <EmptyState
                    icon={<BoltRounded />}
                    title="No devices assigned"
                    description="An administrator assigns endpoints to your account."
                    minHeight={180}
                  />
                )}
                {operatorDevices.map((device) => {
                  const actions = (device.operation_buttons || []).filter((button) => button.code);
                  return (
                    <Box
                      key={device.device_id}
                      sx={{
                        p: 1.75,
                        borderRadius: 3,
                        border: `1px solid ${alpha(palette.primary, 0.12)}`,
                        background: `linear-gradient(145deg, ${alpha('#0E1730', 0.6)}, ${alpha('#070C1C', 0.5)})`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25, flexWrap: 'wrap' }}>
                        <Typography className="mono" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          {device.device_id}
                        </Typography>
                        <StatusPill
                          label={device.status}
                          tone={device.status === 'online' ? 'success' : device.status === 'updating' ? 'warning' : 'danger'}
                          pulse={false}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Button
                          size="small"
                          onClick={() => navigate(`/devices/${device.device_id}`)}
                          sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                          endIcon={<OpenInFullRounded sx={{ fontSize: 13 }} />}
                        >
                          Profile
                        </Button>
                      </Box>

                      {actions.length === 0 ? (
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                          No remote actions are enabled for this device.
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {actions.map((action) => (
                            <Button
                              key={action.code}
                              size="small"
                              variant="outlined"
                              startIcon={<BoltRounded sx={{ fontSize: 14 }} />}
                              onClick={() => runOperation(device, action)}
                              sx={{
                                fontSize: '0.72rem',
                                color: palette.secondary,
                                borderColor: alpha(palette.secondary, 0.4),
                                '&:hover': {
                                  borderColor: palette.secondary,
                                  background: alpha(palette.secondary, 0.12),
                                },
                              }}
                            >
                              {action.label}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </GlassPanel>
          </Grid>

          <Grid item xs={12} lg={5}>
            <GlassPanel sx={{ p: 3, height: '100%' }}>
              <SectionTitle
                title="Device Data Report"
                subtitle="Statistics for the devices assigned to you"
                icon={<InsightsRounded sx={{ fontSize: 18 }} />}
                action={
                  <Button
                    size="small"
                    onClick={() => navigate('/reports')}
                    sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
                    endIcon={<OpenInFullRounded sx={{ fontSize: 13 }} />}
                  >
                    Full report
                  </Button>
                }
              />

              <Box sx={{ mt: 2.25, display: 'grid', gap: 1, gridTemplateColumns: '1fr 1fr' }}>
                {[
                  { label: 'Data points', value: operatorReport?.totals?.data_points || 0, tone: palette.primary },
                  { label: 'Alerts', value: operatorReport?.totals?.alerts || 0, tone: palette.danger },
                  { label: 'Uptime', value: `${operatorReport?.totals?.uptime_pct || 0}%`, tone: palette.success },
                  { label: 'Msgs / hour', value: operatorReport?.totals?.messages_per_hour || 0, tone: palette.secondary },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(item.tone, 0.18)}`,
                      background: alpha(item.tone, 0.05),
                    }}
                  >
                    <Typography sx={{ fontSize: '0.62rem', letterSpacing: '0.12em', color: 'text.secondary' }}>
                      {item.label.toUpperCase()}
                    </Typography>
                    <Typography className="mono" sx={{ fontSize: '1.15rem', fontWeight: 500, color: item.tone, mt: 0.4 }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ mt: 2.5 }}>
                <HoloLineChart
                  labels={(operatorReport?.timeline || []).map((point) => point.label)}
                  datasets={[
                    {
                      label: 'Data points',
                      data: (operatorReport?.timeline || []).map((point) => point.data_points),
                      color: palette.primary,
                    },
                  ]}
                  height={190}
                />
              </Box>

              {(operatorReport?.parameters || []).length > 0 && (
                <>
                  <Divider sx={{ borderColor: alpha(palette.primary, 0.1), my: 2 }} />
                  <Typography sx={{ fontSize: '0.64rem', letterSpacing: '0.14em', color: 'text.secondary', mb: 1 }}>
                    CHANNEL AVERAGES
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.75 }}>
                    {operatorReport.parameters.slice(0, 4).map((parameter) => (
                      <Box key={parameter.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography className="mono" sx={{ fontSize: '0.74rem', flex: 1, color: 'text.secondary' }}>
                          {parameter.name}
                        </Typography>
                        <Typography className="mono" sx={{ fontSize: '0.74rem', color: palette.primary, fontWeight: 600 }}>
                          avg {parameter.avg}
                        </Typography>
                        <Typography className="mono" sx={{ fontSize: '0.7rem', color: 'text.secondary', width: 92, textAlign: 'right' }}>
                          min {parameter.min} / max {parameter.max}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </GlassPanel>
          </Grid>
        </Grid>
      )}

      {/* recent devices */}
      <GlassPanel sx={{ p: 3 }}>
        <SectionTitle
          title="Recent Devices"
          subtitle="Latest 5 endpoints reporting into the platform"
          icon={<OpenInFullRounded sx={{ fontSize: 18 }} />}
          action={
            <Button
              size="small"
              endIcon={<ArrowForwardRounded sx={{ fontSize: 15 }} />}
              onClick={() => navigate('/devices')}
              sx={{ fontSize: '0.74rem', color: 'text.secondary' }}
            >
              All devices
            </Button>
          }
        />

        <Box sx={{ mt: 2.25, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' } }}>
          {recentDevices.length === 0 && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <EmptyState icon={<SensorsRounded />} title="No devices yet" description="Register your first endpoint to begin monitoring." minHeight={160} />
            </Box>
          )}
          {recentDevices.map((device) => (
            <Box
              key={device.device_id}
              onClick={() => navigate(`/devices/${device.device_id}`)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                p: 1.75,
                borderRadius: 3,
                cursor: 'pointer',
                border: `1px solid ${alpha(palette.primary, 0.12)}`,
                background: `linear-gradient(145deg, ${alpha('#0E1730', 0.62)}, ${alpha('#070C1C', 0.5)})`,
                transition: 'transform 240ms cubic-bezier(0.22,0.9,0.28,1), border-color 240ms ease',
                '&:hover': { transform: 'translateY(-3px)', borderColor: alpha(palette.primary, 0.4) },
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  borderRadius: 2.25,
                  display: 'grid',
                  placeItems: 'center',
                  background: alpha(palette.primary, 0.12),
                  border: `1px solid ${alpha(palette.primary, 0.26)}`,
                  boxShadow: `inset 0 0 18px ${alpha(palette.primary, 0.16)}`,
                }}
              >
                <SensorsRounded sx={{ fontSize: 19, color: palette.primary }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography className="mono" sx={{ fontSize: '0.79rem', fontWeight: 600, noWrap: 'ellipsis', overflow: 'hidden' }}>
                  {device.device_id}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', noWrap: 'ellipsis', overflow: 'hidden' }}>
                  {device.device_type} · fw {device.firmware_version}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <StatusPill
                  label={device.status}
                  tone={device.status === 'online' ? 'success' : device.status === 'updating' ? 'warning' : 'danger'}
                />
                <Typography sx={{ fontSize: '0.63rem', color: 'text.secondary', mt: 0.6 }}>
                  {relativeTime(device.last_seen)}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </GlassPanel>
    </Box>
  );
}
